import {
  deleteCard,
  initiateCornerDownload,
} from '../../data/update_operations.js';
import stateManager from '../../data/state_manager.js';
import { LOG_LEVELS, logMessage } from '../../logging.js';
import { ListingFormPopupController } from './listing_form_popup_controller.js';
import { RedditFormPopupController } from './reddit_form_popup_controller';
import { ImageCarouselModel } from '../../ui_models/image_carousel_model';
import { ImageCarouselPopup } from '../../ui_components/popups/image_carousel_popup.js';
import { fetchStreamingData } from '../../data/streaming.js';
import config from '../../../config/index.js';
import { sendAuthenticatedRequest } from '../../auth.js';
import { hideOverlay, showOverlay } from '../../ui_components/overlay.js';
import { fetchCardData } from '../../data/update_operations.js';
import { getItemPrice } from '../../utils/price_utils.js';
import { getEbayItemUrl } from '../../utils/ebay_url_utils.js';

class CardDisplayPopupController {
  constructor(tour, card_info, is_public) {
    this.showCardDisplayPopup = this.showCardDisplayPopup.bind(this);

    this.model = new ImageCarouselModel(is_public);
    this.view = new ImageCarouselPopup('carousel');
    this.card_info = card_info;
    this.is_public = is_public;
    this.tour = tour;
    this.bodyScrollState = null;
    this.cornerBrowser = null;
  }

  async showCardDisplayPopup(matched_items, priced_items, headers) {
    if (this.is_public) {
      this.addCardImagesToModel();

      this.initialize();
      this.hideCarouselControls(this.view);
      this.addDisplayPopupEventListeners();
      this.view.addDots(this.model.numImages());

      logMessage(LOG_LEVELS.INFO, 'Card display popup shown');
    } else {
      this.model.setHeaders(headers);
      logMessage(LOG_LEVELS.DEBUG, 'Showing card display popup');

      this.addCardImagesToModel();

      this.initialize();
      this.hideCarouselControls(this.view);
      this.addDisplayPopupEventListeners();
      this.view.addDots(this.model.numImages());
      this.renderRichCardDetails(matched_items, priced_items, headers);

      logMessage(LOG_LEVELS.INFO, 'Card display popup shown');
    }
  }

  renderRichCardDetails(matchedItems, pricedItems, headers) {
    this.view.popup.classList.add('card-detail-popup');
    const imageContainer = this.view.popup.querySelector(
      '.single-image-container'
    );
    imageContainer?.classList.add('card-detail-media');

    const details = document.createElement('section');
    details.className = 'card-detail-content';
    details.append(
      this.createAnalysisSection(),
      this.createCompsSection(matchedItems, pricedItems, headers),
      this.createActionsSection(headers)
    );
    this.view.popup.append(details);
  }

  createAnalysisSection() {
    const reference = this.card_info.reference_card || {};
    const userCard = this.card_info.user_card || {};
    const subjects = Array.isArray(reference.subjects)
      ? reference.subjects
      : reference.players;
    const subjectText = Array.isArray(subjects)
      ? subjects.join(', ')
      : subjects || reference.player || 'Unknown card subject';

    const section = document.createElement('section');
    section.className = 'card-detail-section card-analysis';

    const heading = document.createElement('div');
    heading.className = 'card-detail-heading';
    const headingContent = document.createElement('div');
    const eyebrow = document.createElement('span');
    eyebrow.className = 'card-detail-eyebrow';
    eyebrow.textContent = 'Original analysis';
    const title = document.createElement('h2');
    title.textContent = subjectText;
    headingContent.append(eyebrow, title);
    heading.append(headingContent);
    section.append(heading);

    const analysisGrid = document.createElement('dl');
    analysisGrid.className = 'card-analysis-grid';
    [
      ['Year', reference.year],
      ['Category', reference.card_type],
      ['Card type', reference.trading_card_type],
      ['Program', reference.program],
      ['Set', reference.release_set],
      ['Parallel', reference.parallel],
      ['Card #', reference.number],
      ['Team', reference.team],
      ['Position', reference.position],
      ['Condition', userCard.condition],
      ['Grade', this.getGradeLabel(reference)],
    ].forEach(([label, value]) => {
      if (value === undefined || value === null || value === '') return;
      const item = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = String(value);
      item.append(term, description);
      analysisGrid.append(item);
    });
    section.append(analysisGrid);
    return section;
  }

  getGradeLabel(reference) {
    if (!reference.grading_company && !reference.grade) return null;
    return [reference.grading_company, reference.grade]
      .filter(Boolean)
      .join(' ');
  }

  createCompsSection(matchedItems, pricedItems, headers) {
    const section = document.createElement('section');
    section.className = 'card-detail-section card-detail-comps';

    const heading = document.createElement('div');
    heading.className = 'card-detail-heading card-detail-heading--row';
    const headingText = document.createElement('div');
    headingText.innerHTML = `
      <span class="card-detail-eyebrow">Market data</span>
      <h3>Comparable listings</h3>
    `;
    const refreshButton = this.createActionButton(
      'Refresh',
      'fas fa-rotate',
      () => this.refreshInlineComps(section, headers),
      'card-detail-refresh'
    );
    heading.append(headingText, refreshButton);

    const lists = document.createElement('div');
    lists.className = 'card-detail-comp-columns';
    lists.append(
      this.createCompList('Sold', pricedItems),
      this.createCompList('Active', matchedItems)
    );
    const marketAnalysis = this.createMarketAnalysisSummary();
    section.append(heading);
    if (marketAnalysis) {
      section.append(marketAnalysis);
    }
    section.append(lists);
    return section;
  }

  createMarketAnalysisSummary() {
    const analysis = this.getStoredMarketAnalysis();
    if (!analysis) {
      const unavailable = document.createElement('div');
      unavailable.className =
        'card-market-analysis card-market-analysis--empty';
      unavailable.textContent =
        'Acceptance-time market analysis is unavailable for this card.';
      return unavailable;
    }

    const summary = document.createElement('div');
    summary.className = 'card-market-analysis';

    const header = document.createElement('div');
    header.className = 'card-market-analysis__header';
    const label = document.createElement('span');
    label.className = 'card-detail-eyebrow';
    label.textContent = 'Pricing analysis';
    header.append(label);

    const range = this.formatAnalysisRange(analysis);
    if (range) {
      const rangeElement = document.createElement('strong');
      rangeElement.className = 'card-market-analysis__range';
      rangeElement.textContent = range;
      header.append(rangeElement);
    }

    const trend = this.createTrendBadge(analysis.trend);
    if (trend) {
      header.append(trend);
    }
    summary.append(header);

    if (analysis.pricing_reasoning) {
      const reasoning = document.createElement('p');
      reasoning.className = 'card-market-analysis__reasoning';
      reasoning.textContent = analysis.pricing_reasoning;
      summary.append(reasoning);
    }

    const factors = Array.isArray(analysis.key_factors)
      ? analysis.key_factors
      : [];
    if (factors.length) {
      const factorList = document.createElement('ul');
      factorList.className = 'card-market-analysis__factors';
      factors.forEach((factor) => {
        const item = document.createElement('li');
        item.textContent = factor;
        factorList.append(item);
      });
      summary.append(factorList);
    }

    if (analysis.trend_reasoning) {
      const trendReasoning = document.createElement('p');
      trendReasoning.className = 'card-market-analysis__trend-reasoning';
      trendReasoning.textContent = analysis.trend_reasoning;
      summary.append(trendReasoning);
    }

    return summary;
  }

  getStoredMarketAnalysis() {
    const candidates = [
      this.card_info.market_analysis,
      this.card_info.estimated_comps,
      this.card_info.user_card?.market_analysis,
      this.card_info.user_card?.estimated_comps,
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;
      if (typeof candidate === 'object') return candidate;
      if (typeof candidate === 'string') {
        try {
          return JSON.parse(candidate);
        } catch {
          // Ignore malformed legacy values.
        }
      }
    }
    return null;
  }

  formatAnalysisRange(analysis) {
    const low = Number(analysis.estimated_value_low);
    const high = Number(analysis.estimated_value_high);
    if (!Number.isFinite(low) && !Number.isFinite(high)) return null;

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: analysis.currency || 'USD',
      maximumFractionDigits: 2,
    });
    if (Number.isFinite(low) && Number.isFinite(high)) {
      return `${formatter.format(low)} – ${formatter.format(high)}`;
    }
    return formatter.format(Number.isFinite(low) ? low : high);
  }

  createTrendBadge(trend) {
    if (!trend) return null;
    const normalized = String(trend).toLowerCase();
    const labels = {
      up: 'Trending up',
      flat: 'Holding flat',
      down: 'Trending down',
      unknown: 'Trend unclear',
    };
    const badge = document.createElement('span');
    badge.className = `card-market-analysis__trend card-market-analysis__trend--${
      labels[normalized] ? normalized : 'unknown'
    }`;
    badge.textContent = labels[normalized] || labels.unknown;
    return badge;
  }

  createCompList(label, items = []) {
    const group = document.createElement('div');
    group.className = 'card-detail-comp-group';
    const heading = document.createElement('h4');
    heading.textContent = `${label} (${items.length})`;
    group.append(heading);

    const list = document.createElement('div');
    list.className = 'card-detail-comp-list';
    if (!items.length) {
      const empty = document.createElement('p');
      empty.className = 'card-detail-empty';
      empty.textContent = 'No comps found';
      list.append(empty);
    } else {
      items.forEach((item) => list.append(this.createCompRow(item)));
    }
    group.append(list);
    return group;
  }

  createCompRow(item) {
    const row = document.createElement('a');
    row.className = 'card-detail-comp-row';
    row.href = getEbayItemUrl(item);
    row.target = '_blank';
    row.rel = 'noopener noreferrer';

    const price = getItemPrice(item);
    const priceElement = document.createElement('strong');
    priceElement.textContent =
      price === null
        ? '—'
        : new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2,
          }).format(price);
    const title = document.createElement('span');
    title.textContent = item.title || 'View eBay listing';
    title.title = item.title || 'View eBay listing';
    row.append(priceElement, title);
    return row;
  }

  async refreshInlineComps(section, headers) {
    const button = section.querySelector('.card-detail-refresh');
    button.disabled = true;
    button.querySelector('span').textContent = 'Refreshing…';
    try {
      const response = await fetchCardData(
        `${config.detectionUrl}/refresh-comps`,
        'POST',
        { ...headers, 'Content-Type': 'application/json' },
        { user_card_id: this.card_info.user_card.user_card_id }
      );
      if (!response.ok) {
        throw new Error(response.body?.error || 'Unable to refresh comps.');
      }

      const pricedItems = response.body.priced_items || {};
      const matchedItems = response.body.matched_items || {};
      const columns = section.querySelector('.card-detail-comp-columns');
      columns.replaceChildren(
        this.createCompList('Sold', this.extractCompItems(pricedItems)),
        this.createCompList('Active', this.extractCompItems(matchedItems))
      );
    } catch (error) {
      alert(error.message);
    } finally {
      button.disabled = false;
      button.querySelector('span').textContent = 'Refresh';
    }
  }

  extractCompItems(collection) {
    if (Array.isArray(collection)) return collection;
    if (!collection || typeof collection !== 'object') return [];
    const firstValue = Object.values(collection)[0];
    return Array.isArray(firstValue) ? firstValue : [];
  }

  createActionsSection(headers) {
    const section = document.createElement('section');
    section.className = 'card-detail-section card-detail-actions';
    section.innerHTML = `
      <div class="card-detail-heading">
        <span class="card-detail-eyebrow">Actions</span>
        <h3>What would you like to do?</h3>
      </div>
    `;

    const grid = document.createElement('div');
    grid.className = 'card-action-grid';
    grid.append(
      this.createActionButton('List on eBay', 'fab fa-ebay', () =>
        this.listCardEvent(this.card_info, headers)
      ),
      this.createActionButton('Post on Reddit', 'fab fa-reddit', () =>
        this.postOnRedditEvent(this.card_info, headers)
      ),
      this.createActionButton('See corners', 'fas fa-expand', () =>
        this.seeCornersEvent(this.card_info, headers)
      ),
      this.createActionButton(
        'Delete card',
        'fas fa-trash',
        () => this.deleteCardEvent(this.card_info, headers),
        'card-action-button--danger'
      )
    );
    section.append(grid);
    return section;
  }

  createActionButton(label, iconClass, action, extraClass = '') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `card-action-button ${extraClass}`.trim();
    const icon = document.createElement('i');
    icon.className = iconClass;
    icon.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.textContent = label;
    button.append(icon, text);
    button.addEventListener('click', action);
    return button;
  }

  addCardImagesToModel() {
    const { front_image_url_large } = this.card_info.user_card;

    if (front_image_url_large) {
      this.model.addImage(front_image_url_large);
    }
  }

  hideCarouselControls(popupManager) {
    popupManager.hideArrows();
    popupManager.hideDots();
  }

  addDisplayPopupEventListeners() {
    this.setupCloseButton();
    logMessage('INFO', 'Event listeners added to popup');
  }

  setupCloseButton() {
    this.view.bindCloseButton(() => this.closePopup());
  }

  closePopup() {
    this.clearCornerBrowser();
    this.view.remove();
    this.unlockBodyScroll();
    stateManager.updateState({ popupOnDisplay: false, reinitialize: true });
  }

  async initialize() {
    try {
      this.lockBodyScroll();
      const imageUrl = await this.model.getImage(0);
      this.view.updateImage(imageUrl);
      this.view.setActiveDot(0);
    } catch (error) {
      console.error('Error initializing:', error);
      this.unlockBodyScroll();
      return false;
    }
  }

  lockBodyScroll() {
    const bodyStyle = document.body.style;
    this.bodyScrollState = {
      top: bodyStyle.top,
      position: bodyStyle.position,
      width: bodyStyle.width,
      scrollY: window.scrollY,
    };
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${this.bodyScrollState.scrollY}px`;
    bodyStyle.width = '100%';
  }

  unlockBodyScroll() {
    if (!this.bodyScrollState) {
      return;
    }

    const { top, position, width, scrollY } = this.bodyScrollState;
    const bodyStyle = document.body.style;
    bodyStyle.position = position || '';
    bodyStyle.top = top || '';
    bodyStyle.width = width || '';
    window.scrollTo(0, scrollY || 0);
    this.bodyScrollState = null;
  }

  async seeCornersEvent(card_info, headers) {
    const front_image_url_parent =
      this.card_info.user_card.front_image_url_parent;
    const front_coordinates = this.card_info.user_card.front_coordinates
      ? JSON.parse(this.card_info.user_card.front_coordinates)
      : null;

    if (!front_image_url_parent || !front_coordinates) {
      alert(
        'This card was scanned in an early release, so stored corner data is unavailable.'
      );
    } else {
      showOverlay();
      const payload = {
        user_card_id: card_info.user_card.user_card_id,
      };

      const response = await initiateCornerDownload(
        headers,
        payload,
        this.handleNoSubscription
      );
      const confirmResponse = await this.handleDownloadConflict(
        response,
        headers,
        payload
      );

      if (confirmResponse.userDecision && confirmResponse.response.ok) {
        const requestId = confirmResponse.response.body.request_id;
        const controller = new AbortController();

        fetchStreamingData(
          `${config.detectionUrl}/get-data-stream/${requestId}`,
          headers,
          (data) => this.handleCornerImagesReady(data, headers, requestId),
          controller.signal
        );
      }
    }
  }

  async isEbayAccountConnected(headers) {
    try {
      const response = await fetchCardData(
        `${config.detectionUrl}/check-ebay-connection`,
        'GET',
        headers,
        null
      );

      return response.body.connected;
    } catch (error) {
      console.error('Error checking eBay connection:', error);
      return false;
    }
  }

  async isRedditAccountConnected(headers) {
    try {
      const response = await fetchCardData(
        `${config.detectionUrl}/check-reddit-connection`,
        'GET',
        headers,
        null
      );

      return response.body.connected;
    } catch (error) {
      console.error('Error checking reddit connection:', error);
      return false;
    }
  }

  async listCardEvent(card_info, headers) {
    if (await this.isEbayAccountConnected(headers)) {
      let listContrller = new ListingFormPopupController(card_info, headers);
      listContrller.showListingPopup(this);
    } else {
      alert(
        'Please connect your eBay account under the Integrations menu option.'
      );
    }
  }

  async postOnRedditEvent(card_info, headers) {
    if (await this.isRedditAccountConnected(headers)) {
      let listContrller = new RedditFormPopupController(card_info, headers);
      listContrller.showListingPopup(this);
    } else {
      alert(
        'Please connect your reddit account under the Integrations menu option.'
      );
    }
  }

  async handleDownloadConflict(response, headers, payload) {
    if (response.status === 409) {
      const userDecision = confirm(
        'View up to 5 sets of corner images for free. Continue?'
      );
      if (userDecision) {
        payload.user_decision = true;
        const res = await initiateCornerDownload(
          headers,
          payload,
          this.handleNoSubscription,
          true
        );
        return { response: res, userDecision: true };
      } else {
        hideOverlay();
        return { response, userDecision: false };
      }
    }
    return { response, userDecision: true };
  }

  async handleCornerImagesReady(data, headers, requestId) {
    if (!Array.isArray(data.corner_images) || !data.corner_image_id) {
      return;
    }

    this.clearCornerBrowser();
    this.cornerBrowser = {
      headers,
      requestId,
      imageSetId: data.corner_image_id,
      images: data.corner_images,
      index: 0,
      objectUrls: new Map(),
    };

    const section = this.createCornerBrowser();
    const actions = this.view.popup.querySelector('.card-detail-actions');
    actions?.before(section);

    try {
      await this.showCornerImage(0);
      section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (error) {
      alert('Unable to load the corner images. Please try again.');
      logMessage(LOG_LEVELS.ERROR, 'Unable to show corner images', {
        error: error.toString(),
      });
    } finally {
      hideOverlay();
    }
  }

  createCornerBrowser() {
    const section = document.createElement('section');
    section.className = 'card-detail-section card-corner-browser';
    section.innerHTML = `
      <div class="card-detail-heading">
        <span class="card-detail-eyebrow">Corner viewer</span>
        <h3>Card corners</h3>
      </div>
      <div class="card-corner-stage">
        <img class="card-corner-image" alt="" />
      </div>
      <div class="card-corner-controls">
        <button type="button" class="card-corner-nav card-corner-prev" aria-label="Previous corner">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
        <span class="card-corner-label" aria-live="polite"></span>
        <button type="button" class="card-corner-nav card-corner-next" aria-label="Next corner">
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    `;
    section
      .querySelector('.card-corner-prev')
      .addEventListener('click', () => this.changeCornerImage(-1));
    section
      .querySelector('.card-corner-next')
      .addEventListener('click', () => this.changeCornerImage(1));
    return section;
  }

  async changeCornerImage(offset) {
    if (!this.cornerBrowser) return;
    const imageCount = this.cornerBrowser.images.length;
    const nextIndex =
      (this.cornerBrowser.index + offset + imageCount) % imageCount;
    await this.showCornerImage(nextIndex);
  }

  async showCornerImage(index) {
    const browser = this.cornerBrowser;
    if (!browser) return;

    const image = browser.images[index];
    let objectUrl = browser.objectUrls.get(image.filename);
    if (!objectUrl) {
      const url = `${config.detectionUrl}/corner-image/${encodeURIComponent(
        browser.requestId
      )}/${encodeURIComponent(browser.imageSetId)}/${encodeURIComponent(
        image.filename
      )}`;
      const response = await sendAuthenticatedRequest(url, {
        method: 'GET',
        headers: browser.headers,
        responseType: 'blob',
      });
      if (!response.ok) {
        throw new Error(`Corner image request failed: ${response.status}`);
      }
      objectUrl = window.URL.createObjectURL(response.blob);
      browser.objectUrls.set(image.filename, objectUrl);
    }

    browser.index = index;
    const section = this.view.popup.querySelector('.card-corner-browser');
    const imageElement = section.querySelector('.card-corner-image');
    imageElement.src = objectUrl;
    imageElement.alt = image.label;
    section.querySelector('.card-corner-label').textContent =
      `${image.label} · ${index + 1} of ${browser.images.length}`;
  }

  clearCornerBrowser() {
    if (!this.cornerBrowser) return;
    this.cornerBrowser.objectUrls.forEach((url) =>
      window.URL.revokeObjectURL(url)
    );
    this.view.popup
      ?.querySelector('.card-corner-browser')
      ?.remove();
    this.cornerBrowser = null;
  }

  handleNoSubscription() {
    hideOverlay();
    document.getElementById('upgradeModal').style.display = 'block';
    // alert('You have exceeded the allowed number of downloads.')
  }

  async deleteCardEvent(card_info, headers) {
    if (await deleteCard(card_info, headers)) {
      this.closePopup();
    }
  }

  async updateFrontImage(newLocalUrl, new_card_info) {
    this.card_info = new_card_info;
    this.model.updateLocalImageAtIndex(0, newLocalUrl);

    if (this.model.getCurrentIndex() == 0) {
      this.view.updateImage(newLocalUrl);
    }
  }
}

export { CardDisplayPopupController };
